import React from 'react';
import { cn } from '../../lib/utils';

export const TrustScoreRing = ({ score, size = 'md', showLabel = true, animated = true, className }) => {
  const sizes = {
    xs: { ring: 48, stroke: 4, text: 'text-base', inner: 'text-[9px]' },
    sm: { ring: 60, stroke: 5, text: 'text-lg', inner: 'text-[10px]' },
    md: { ring: 80, stroke: 6, text: 'text-2xl', inner: 'text-xs' },
    lg: { ring: 120, stroke: 8, text: 'text-4xl', inner: 'text-sm' },
    xl: { ring: 160, stroke: 10, text: 'text-5xl', inner: 'text-base' }
  };

  const { ring, stroke, text, inner } = sizes[size] || sizes.md;
  const radius = (ring - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = ((score || 0) / 100) * circumference;

  const getScoreColor = (s) => {
    if (s >= 80) return { stroke: '#14B8A6', glow: 'rgba(20, 184, 166, 0.5)' };
    if (s >= 60) return { stroke: '#FBBF24', glow: 'rgba(251, 191, 36, 0.5)' };
    return { stroke: '#EF4444', glow: 'rgba(239, 68, 68, 0.5)' };
  };

  const colors = getScoreColor(score);

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg 
        width={ring} 
        height={ring} 
        className="transform -rotate-90"
        style={{ filter: `drop-shadow(0 0 10px ${colors.glow})` }}
      >
        {/* Background ring */}
        <circle
          cx={ring / 2}
          cy={ring / 2}
          r={radius}
          fill="none"
          stroke="rgba(51, 65, 85, 0.5)"
          strokeWidth={stroke}
        />
        {/* Progress ring */}
        <circle
          cx={ring / 2}
          cy={ring / 2}
          r={radius}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          className={animated ? 'trust-ring-animated' : ''}
          style={{ 
            transition: animated ? 'stroke-dashoffset 1s ease-out' : 'none',
            strokeDashoffset: animated ? undefined : circumference - progress
          }}
        />
      </svg>
      {showLabel && (
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span 
            className={cn('font-mono font-bold', text)}
            style={{ color: colors.stroke }}
          >
            {score || 0}
          </span>
        </div>
      )}
    </div>
  );
};

export const TrustScoreBar = ({ label, score, className }) => {
  const getBarColor = (s) => {
    if (s >= 80) return 'bg-teal-500';
    if (s >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-slate-300">{label}</span>
        <span className="font-mono font-semibold text-white">{score}</span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-700 ease-out', getBarColor(score))}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

export const TrustScoreBreakdown = ({ trustScore, className }) => {
  const components = [
    { key: 'profile_quality', label: 'Profile Quality' },
    { key: 'job_performance', label: 'Job Performance' },
    { key: 'response_rate', label: 'Response Rate' },
    { key: 'client_reviews', label: 'Client Reviews' },
    { key: 'time_on_platform', label: 'Time on Platform' }
  ];

  return (
    <div className={cn('space-y-5', className)}>
      {components.map(({ key, label }) => (
        <TrustScoreBar
          key={key}
          label={label}
          score={trustScore?.[key] || 0}
        />
      ))}
    </div>
  );
};

export const DataMaturityBadge = ({ maturity, className }) => {
  const config = {
    new: { label: 'NEW', color: 'bg-slate-800 text-slate-400 border-slate-700' },
    growing: { label: 'GROWING', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' },
    established: { label: 'ESTABLISHED', color: 'bg-teal-500/10 text-teal-400 border-teal-500/30' },
    veteran: { label: 'VETERAN', color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' }
  };

  const { label, color } = config[maturity] || config.new;

  return (
    <span className={cn(
      'inline-flex items-center px-2.5 py-1 rounded text-xs font-mono font-semibold uppercase tracking-wider border',
      color,
      className
    )}>
      {label}
    </span>
  );
};

export const TrustScoreHero = ({ score, className }) => {
  return (
    <div className={cn('relative', className)}>
      {/* Outer glow ring */}
      <div 
        className="absolute inset-0 rounded-full animate-glow-pulse"
        style={{
          background: 'radial-gradient(circle, rgba(20, 184, 166, 0.2) 0%, transparent 70%)',
          transform: 'scale(1.6)'
        }}
      />
      {/* Inner ambient glow */}
      <div 
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(20, 184, 166, 0.1) 0%, transparent 50%)',
          transform: 'scale(1.3)'
        }}
      />
      <TrustScoreRing score={score} size="xl" animated />
    </div>
  );
};
